import { gql } from '@apollo/client';
import { useEffect, useMemo, useState } from 'react';
import { MstHinInfo, MstHinmei, MstHinmeiFree } from 'src/common/interfaces';
import { MitsumoriType, UseType } from 'src/common/number-enum';
import { getMstHinInfo, getMstHinmei } from 'src/graphql/queries';
import { useAppLazyQuery } from 'src/libs/hooks';

const ModalGuideHinmei = ({
  setIsOpen,
  primaryCd,
  secondaryCd,
  productCd,
  contractPeriod,
  hinmeiKb,
  onCallbackFunction,
}: {
  setIsOpen: (isOpen: boolean) => void;
  primaryCd: number;
  secondaryCd: number;
  productCd: number;
  contractPeriod: number;
  hinmeiKb: MitsumoriType;
  onCallbackFunction: (hinmeiList: Array<MstHinmei> | MstHinmeiFree) => void;
}) => {
  const header = ['選択', '品名コード', '品名'];
  const hinmeiKbEnum = [
    { value: 1, label: 'ソフトウェア' },
    { value: 2, label: 'テンプレート' },
    { value: 3, label: 'ライセンス' },
    { value: 4, label: 'ユースウェア' },
    { value: 5, label: 'KDSS保守' },
  ];
  const [dataMstHinmei, setDataMstHinmei] = useState<Array<MstHinmei>>([]);
  const [mstHinmeiCd, setMstHinmeiCd] = useState<string>('');
  const [mstHinmeiMn, setMstHinmeiMn] = useState<string>('');

  const [searchMstHinmei, { data: mstHinmeiData, loading: isMstHinmeiLoading }] = useAppLazyQuery({
    query: gql(getMstHinmei),
  });
  const [searchMstHinInfo, { data: mstHinInfoData, loading: isMstHinInfoLoading }] = useAppLazyQuery({
    query: gql(getMstHinInfo),
  });

  const renderTableHeader = () => {
    return header.map((key, index) => (
      <th
        className="whitespace-nowrap p-[8px] sticky top-0 border-[1px] border-solid border-[#ddd] pt-[12px] pb-[12px] text-center bg-blue-400 text-[#fff]"
        key={index}
      >
        {key.toUpperCase()}
      </th>
    ));
  };

  const renderTableData = useMemo(() => {
    if (dataMstHinmei?.length <= 0 || !dataMstHinmei) return;
    return dataMstHinmei?.map((mstHinmei: MstHinmei, index: number) => {
      return (
        <tr key={index}>
          <td className="p-[8px] border-[1px] border-solid border-[#ddd]">
            <p>
              <input
                type="checkbox"
                checked={mstHinmei?.isSelected}
                onChange={() => onHandleChangeSelect(mstHinmei?.mh_hin_cd)}
              />
            </p>
          </td>
          <td className="p-[8px] border-[1px] border-solid border-[#ddd]">
            <p>{mstHinmei?.mh_hin_cd}</p>
          </td>
          <td className="p-[8px] border-[1px] border-solid border-[#ddd]">
            <p>{mstHinmei?.mh_hin_nm}</p>
          </td>
        </tr>
      );
    });
  }, [dataMstHinmei]);

  const onPressSearch = async () => {
    await searchMstHinInfo({
      variables: {
        input: {
          mhi_primary_cd: primaryCd,
          mhi_secondary_cd: secondaryCd,
          mhi_product_cd: productCd,
          mhi_years: contractPeriod,
        },
      },
    });

    const paramsHinmei = {
      mh_hin_cd: mstHinmeiCd === '' ? null : mstHinmeiCd,
      mh_hin_nm: mstHinmeiMn,
      mh_hin_kb: hinmeiKb,
      mh_available_kb: UseType.POSSIBLE,
    };
    await searchMstHinmei({ variables: { input: paramsHinmei } });
  };

  const onHandleFree = () => {
    const param: MstHinmeiFree = {
      mh_hin_cd: '-1',
      mh_hin_nm: '',
    };
    setMstHinmeiMn('');
    setMstHinmeiCd('-1');
    onCallbackFunction(param);
    setIsOpen(false);
  };

  const onHandleChangeSelect = (hinmeiCd: string) => {
    const newList = [...dataMstHinmei];
    newList?.map((hinmei: MstHinmei) => {
      if (hinmei?.mh_hin_cd === hinmeiCd) {
        hinmei.isSelected = !hinmei?.isSelected;
      }
      return hinmei;
    });
    setDataMstHinmei(newList);
  };

  const onHandleImport = () => {
    const importList = dataMstHinmei?.filter((hinmei) => {
      return hinmei.isSelected === true;
    });
    onCallbackFunction(importList);
    setIsOpen(false);
  };

  useEffect(() => {
    const newMstHinmeiData = { ...mstHinmeiData };
    const newMstHinInfoData = { ...mstHinInfoData };
    const listMstHinmei: MstHinmei[] =
      newMstHinmeiData?.getMstHinmei?.sort((hinmei1: any, hinmei2: any) => {
        return hinmei1.mh_hin_cd - hinmei2.mh_hin_cd;
      }) || [];
    const resMstHinmei: MstHinmei[] = [];

    listMstHinmei?.forEach((hinmei: MstHinmei) => {
      hinmei.isSelected = false;

      const hinInfo = newMstHinInfoData?.getMstHinInfo?.filter(
        (item: MstHinInfo) => item.mhi_hin_cd === Number(hinmei.mh_hin_cd)
      );

      if (hinInfo.length !== 0 && hinInfo[0].mhi_available_kb === UseType.POSSIBLE) {
        // 品名紐付け情報があって、使用可の品名だけを抽出する
        resMstHinmei.push(hinmei);
      }
    });
    setDataMstHinmei(resMstHinmei);
  }, [mstHinmeiData]);

  return (
    <div className="fixed w-full h-full inset-0 flex items-center justify-center">
      <div className="bg-[#00000030] w-full h-full z-[0] inset-0 fixed" />
      <div className="flex fixed bg-white h-5/6 w-1/2 items-center flex-col z-[1] overflow-auto">
        <div className="w-5/6 flex justify-end items-center mt-[10px]">
          <button onClick={() => setIsOpen(false)}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-6 h-6"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="h-[40px] w-4/5 flex justify-center items-center border border-zinc-300 mt-[10px]">
          <div className="h-full w-2/5 flex justify-center items-center bg-blue-400 text-white">品名コード</div>
          <div className="h-full w-3/5 flex justify-center items-center">
            <input
              className="bg-transparent pl-[5px] h-[40px] w-full pr-[5px] "
              onChange={(event) => {
                if (event?.target?.value?.length > 8) return;
                setMstHinmeiCd(event?.target?.value);
              }}
              type="number"
              name="primary_store_cd"
              value={mstHinmeiCd}
              min={0}
            />
          </div>
        </div>
        <div className="h-[40px] w-4/5 flex justify-center items-center border border-zinc-300 mt-[10px]">
          <div className="h-full w-2/5 flex justify-center items-center bg-blue-400 text-white">品名</div>
          <div className="h-full w-3/5 flex justify-center items-center">
            <input
              className="bg-transparent pl-[5px] h-[40px] w-full"
              onChange={(event) => {
                if (event?.target?.value?.length > 50) return;
                setMstHinmeiMn(event?.target?.value);
              }}
              type="text"
              name="primary_store_nm"
              value={mstHinmeiMn}
            />
          </div>
        </div>
        <div className="h-[40px] w-4/5 flex justify-center items-center border border-zinc-300 mt-[10px]">
          <div className="h-full w-2/5 flex justify-center items-center bg-blue-400 text-white">品名区分</div>
          <div className="h-full w-3/5 flex justify-center items-center">
            <select
              name="hinmeiKubun"
              id="mh_hin_kb"
              className="bg-transparent pl-[5px] h-[40px] w-full bg-gray-300"
              value={hinmeiKb}
              disabled
            >
              {hinmeiKbEnum.map((hinmeiKb, index: number) => {
                return (
                  <option key={index} value={hinmeiKb.value}>
                    {hinmeiKb.label}
                  </option>
                );
              })}
            </select>
          </div>
        </div>
        <div className="h-[40px] w-4/5 flex justify-end items-center mt-[10px] ">
          <button
            onClick={onPressSearch}
            className="border border-zinc-300 h-[40px] w-[100px] justify-center items-center flex"
          >
            <p>検索</p>
          </button>
        </div>
        <div className="h-[40px] w-4/5 flex justify-start items-center mt-[10px] ">
          <button
            onClick={onHandleFree}
            className="border border-zinc-300 h-[40px] w-[100px] justify-center items-center flex mr-[10px]"
          >
            <p>フリー</p>
          </button>
          <button
            onClick={onHandleImport}
            className="border border-zinc-300 h-[40px] w-[100px] justify-center items-center flex"
          >
            <p>挿入</p>
          </button>
        </div>
        <div className="overflow-auto max-h-100 w-4/5 mt-[10px] mb-[10px]">
          <table className="text-center border-collapse border-[3px] border-solid border-[#ddd] w-full td:p-[10px]  ">
            <thead>
              <tr>{renderTableHeader()}</tr>
            </thead>
            <tbody>
              {isMstHinmeiLoading && isMstHinInfoLoading ? (
                <tr>
                  <td
                    colSpan={header.length}
                    className="text-center animate-pulse p-[8px] border-[1px] border-solid border-[#fff]"
                  >
                    Loading...
                  </td>
                </tr>
              ) : (
                renderTableData
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ModalGuideHinmei;
